import { parse } from "date-fns/parse";
import { isValid } from "date-fns/isValid";
import { format } from "date-fns/format";
import { isBoolean, emailValidator } from "#src/util/helpers.ts";
import { type SavablePropertyRule } from "#src/composables/savable-property.composable.ts";

// Savable Property validators
export function validatePresence(message: string): SavablePropertyRule<any> {
  return {
    v() {
      return Boolean(this.model);
    },
    message,
  };
}

export function validateAccountNumber(): SavablePropertyRule<string> {
  return {
    v() {
      const v = this.model;
      return Boolean(v && `${v}`.length >= 5);
    },
    message: "Must be at least 5 digits long",
  };
}

export function validateEmail(): SavablePropertyRule<string> {
  return {
    message: "Must be a valid email",
    v() {
      return emailValidator(this.model);
    },
  };
}

export function validateTin(): SavablePropertyRule<string> {
  return {
    v() {
      const v = this.model;
      if (!(typeof v === "string")) return false;
      if (!v || v.length === 0) return false;

      const noDashTIN = v.replace(/-/g, "");
      return noDashTIN.length === 9;
    },
    message: "Must be a valid TIN",
  };
}

export function isExternallyValid(): SavablePropertyRule<any> {
  return {
    v() {
      return this.externallyValid === true;
    },
    message: "Must be valid",
  };
}

export function validateRoutingNumber(): SavablePropertyRule<string> {
  return {
    v() {
      const v = this.model;
      return Boolean(v && `${v}`.length === 9);
    },
    message: "Must be 9 digits long",
  };
}

export function validateBoolean({
  mustBeTrue = false,
} = {}): SavablePropertyRule<boolean> {
  return {
    v() {
      if (mustBeTrue) return this.model === true;
      return isBoolean(this.model);
    },
    message: "Please specify",
  };
}

function isNumber(v: unknown) {
  return typeof v === "number" && !isNaN(v);
}

export function validateText(
  args: {
    minLength?: number;
    maxLength?: number;
    exactLength?: number;
  } = {
    minLength: 1,
    maxLength: 255,
  }
): SavablePropertyRule<string> {
  const { minLength, maxLength, exactLength } = args;
  let message;

  if (isNumber(exactLength)) {
    message = `Must be exactly ${exactLength} characters long`;
  } else if (isNumber(minLength) && isNumber(maxLength)) {
    message = `Must be between ${minLength} and ${maxLength} characters long`;
  } else if (isNumber(minLength)) {
    message = `Must be at least ${minLength} characters long`;
  } else if (isNumber(maxLength)) {
    message = `Must be no more than ${maxLength} characters long`;
  } else {
    throw "Missing Text Configuration";
  }

  return {
    v() {
      let value = this.model;
      if (value?.toString) value = value.toString();
      if (!(typeof value === "string")) return false;
      if (!value || !value?.replace) return false;

      if (minLength && value.replace(/ /g, "").length < minLength) return false;
      if (exactLength && value.length !== exactLength) return false;
      if (maxLength && value.length > maxLength) return false;
      return true;
    },
    message,
  };
}

export function validatePhone(): SavablePropertyRule<string> {
  return {
    v() {
      try {
        const v = this.model;
        if (!(typeof v === "string")) return false;
        return Boolean(v && /\([2-9]{1}[0-9]{2}\) [0-9]{3}-[0-9]{4}/.test(v));
      } catch (e) {
        return false;
      }
    },
    message: "Must be a valid phone number not beginning with 1 or 0",
  };
}

interface NumberValidator {
  greaterThan?: null | number;
  greaterThanOrEqualTo?: null | number;
  lessThan?: null | number;
  lessThanOrEqualTo?: null | number;
}

function numberValidator(v: number | null, args?: NumberValidator) {
  const messages = [];
  const shortCircuit = !(typeof v === "number") || Number.isNaN(+v);
  if (!args) {
    if (shortCircuit) messages.push("Required");
    return messages.join(". ");
  }

  const { greaterThan, greaterThanOrEqualTo, lessThan, lessThanOrEqualTo } =
    args;

  if (greaterThan || greaterThan === 0) {
    if (shortCircuit || v <= greaterThan) {
      messages.push(`Must be greater than ${greaterThan}`);
    }
  }
  if (greaterThanOrEqualTo || greaterThanOrEqualTo === 0) {
    if (shortCircuit || v < greaterThanOrEqualTo) {
      messages.push(`Must be greater than or equal to ${greaterThanOrEqualTo}`);
    }
  }
  if (lessThan || lessThan === 0) {
    if (shortCircuit || v >= lessThan) {
      messages.push(`Must be less than ${lessThan}`);
    }
  }
  if (lessThanOrEqualTo || lessThanOrEqualTo === 0) {
    if (shortCircuit || v > lessThanOrEqualTo) {
      messages.push(`Must be less than or equal to ${lessThanOrEqualTo}`);
    }
  }

  return messages.join(". ");
}

export function validateNumber(
  args?: NumberValidator
): SavablePropertyRule<number> {
  return {
    v() {
      const messages = numberValidator(this.model, args);
      return Boolean(!messages.length);
    },
    message() {
      return numberValidator(this.model, args);
    },
  };
}

export function validateIsInteger(): SavablePropertyRule<number> {
  return {
    v() {
      return Number.isInteger(this.model);
    },
    message: "Must be an integer",
  };
}

type ListItem = number | string | unknown;

const isInList = (items: ListItem[], value: ListItem) => {
  return items.some(item => {
    return JSON.stringify(item) === JSON.stringify(value);
  });
};

export function validateInList(
  items: ListItem[],
  message: string | null = null
): SavablePropertyRule<any> {
  return {
    message: message || `Must one of the following: ${items.join(", ")}`,
    v() {
      return isInList(items, this.model);
    },
  };
}

export function validateSsn(): SavablePropertyRule<string> {
  return {
    v() {
      const ssn = this.model;
      if (!(typeof ssn === "string")) return false;
      if (!ssn || ssn.length === 0) {
        return false;
      }
      const noDashSSN = ssn.replace(/-/g, "");
      const match = noDashSSN.match(/^([0-9]{3})([0-9]{2})([0-9]{4})$/);
      if (ssn === "123-45-6789") return false;
      return Boolean(match && noDashSSN.length === 9);
    },
    message: "Must be a valid SSN",
  };
}

// needed unless a global scope is provided?
function parseStandardDateValue(v: string | Date | unknown): Date | null {
  if (v instanceof Date) {
    if (!isValid(v)) return null;
    return v;
  }
  if (typeof v === "string") {
    const date = parse(v, "yyyy-MM-dd", new Date());
    if (!isValid(date)) return null;
    return date;
  }
  return null;
}
interface DateValidationOptions {
  otherDateProperty?: () => string | Date | null;
  isSameOrAfter: boolean;
  isAfter: boolean;
  isSameOrBefore: boolean;
  isBefore: boolean;
  message?: string | (() => string);
}

function timelessDate(a: Date): Date {
  return new Date(a.getFullYear(), a.getMonth(), a.getDate());
}

function validateDateOrder(
  model: string | null,
  args: Partial<DateValidationOptions> = {}
) {
  const errorMessages: string[] = [];
  try {
    const date = parseStandardDateValue(model);
    if (!args.otherDateProperty) {
      if (!date) errorMessages.push("Invalid Date");
      return errorMessages.join(". ");
    }

    const otherDate = parseStandardDateValue(args.otherDateProperty());

    let formatOrToday = "";
    if (otherDate) {
      formatOrToday =
        timelessDate(otherDate).getTime() === timelessDate(new Date()).getTime()
          ? "today"
          : format(otherDate, "MM/dd/yyyy");
    }

    const shortCircuit = !date || !otherDate;

    if (args.isAfter) {
      if (shortCircuit || date.getTime() <= otherDate.getTime()) {
        errorMessages.push(`Must be after ${formatOrToday}`);
      }
    }
    if (args.isSameOrAfter) {
      if (
        shortCircuit ||
        timelessDate(date).getTime() < timelessDate(otherDate).getTime()
      ) {
        errorMessages.push(`Must be on or after ${formatOrToday}`);
      }
    }

    if (args.isBefore) {
      if (shortCircuit || date.getTime() >= otherDate.getTime()) {
        errorMessages.push(`Must be before ${formatOrToday}`);
      }
    }
    if (args.isSameOrBefore) {
      if (
        shortCircuit ||
        timelessDate(date).getTime() > timelessDate(otherDate).getTime()
      ) {
        errorMessages.push(`Must be on or before ${formatOrToday}`);
      }
    }
  } catch (e) {
    errorMessages.push("Invalid Date");
  }

  if (errorMessages.length) {
    if (typeof args.message === "function") return args.message();
    return args.message || errorMessages.join(". ");
  }
  return "";
}

export function validateDate(
  args: Partial<DateValidationOptions> = {}
): SavablePropertyRule<string> {
  return {
    v() {
      return Boolean(!validateDateOrder(this.model, args).length);
    },
    message() {
      return validateDateOrder(this.model, args);
    },
  };
}

export function validateExact(value: any): SavablePropertyRule<any> {
  return {
    v() {
      return this.model === value;
    },
    message: "Required",
  };
}
